import api from './api';
import { unwrapList, unwrapObject, type ListResult } from './api-envelope';
import type {
  B2BCreditLimitsQuery,
  B2BOverdueAlertsQuery,
  B2BReceivableQuery,
  B2BReceivableScopeQuery,
} from './queryKeys';

export type B2BReceivableStatus = 'OPEN' | 'PARTIAL' | 'PAID' | 'OVERDUE' | 'VOID';

export interface B2BReceivableCustomerSnapshot {
  _id: string;
  company_name: string;
  tax_code?: string;
  phone?: string;
}

export interface B2BPaymentRecord {
  amount: number;
  paid_at: string;
  method?: string;
  reference_code?: string;
  note?: string;
  created_by?: string;
}

export interface B2BReceivableType {
  _id: string;
  order_id: string;
  order_code: string;
  invoice_number?: string;
  customer: B2BReceivableCustomerSnapshot;
  original_amount: number;
  paid_amount: number;
  outstanding_amount: number;
  invoice_date: string;
  due_date: string;
  status: B2BReceivableStatus;
  payments?: B2BPaymentRecord[];
  note?: string;
  created_at?: string;
  updated_at?: string;
}

export interface B2BReceivableDashboard {
  total_outstanding: number;
  overdue_amount: number;
  not_due_amount: number;
  invoice_count: number;
  by_status: Array<{ _id: B2BReceivableStatus; count: number; amount: number }>;
  top_customers: Array<{
    _id: string;
    customer_code?: string;
    customer_name?: string;
    company_name: string;
    tax_code?: string;
    outstanding_amount: number;
    overdue_amount: number;
  }>;
  /** % thu hồi 30 ngày qua, đã làm tròn 1 chữ số thập phân (vd 78.4) — KHÔNG phải số 0-1. */
  collection_rate_30d: number;
}

export interface B2BAgingBucket {
  count: number;
  amount: number;
}

/** Bucket "mức độ quá hạn" riêng cho dashboard 4.1 (TCR-1194) — chỉ tính phần đã quá hạn, basis due_date. */
export interface B2BAgingSeverity {
  overdue_0_7: B2BAgingBucket;
  overdue_8_15: B2BAgingBucket;
  overdue_16_30: B2BAgingBucket;
  overdue_over_30: B2BAgingBucket;
}

export interface B2BAgingReport {
  not_due: B2BAgingBucket;
  d0_30: B2BAgingBucket;
  d31_60: B2BAgingBucket;
  d61_90: B2BAgingBucket;
  d90_plus: B2BAgingBucket;
  /** MỚI (TCR-1194) — cộng thêm, không thay thế các field trên (đang phục vụ báo cáo kế toán). */
  severity: B2BAgingSeverity;
}

export interface B2BReceivableOverview {
  dashboard: B2BReceivableDashboard;
  aging: B2BAgingReport;
  overdue_alerts: B2BOverdueAlert[];
}

export interface B2BOverdueAlert {
  customer_id: string;
  customer_code?: string;
  customer_name?: string;
  company_name: string;
  tax_code: string;
  phone: string;
  days_overdue: number;
  total_overdue_amount: number;
  invoice_count: number;
  last_order_date: string | null;
  latest_debt_reminder?: { created_by_name: string; created_at: string } | null;
  /** NVKD phụ trách — null nếu chưa gán. */
  salesperson: { id: string; name: string } | null;
}

export interface B2BDebtReminder {
  _id: string;
  customer_id: string;
  subject: string;
  content: string;
  created_by_name: string;
  created_at: string;
}

export interface B2BReceivableStaffOption {
  id: string;
  name: string;
  staff_code: string | null;
  phone: string | null;
}

export interface B2BCreditLimit {
  customer_id: string;
  credit_limit: number;
  current_debt: number;
  /** So công nợ HIỆN TẠI với hạn mức — chưa tính đơn nào đang chờ duyệt. */
  is_over_limit: boolean;
  available_credit: number;
  /**
   * TCR-1883 — tenant tắt công tắc "Kiểm tra hạn mức công nợ khi lên đơn B2B".
   * Khi đó `credit_limit` bằng 0 nhưng KHÔNG có khái niệm "vượt hạn mức".
   */
  is_unlimited?: boolean;
  /** Hạn mức này ở đâu ra — cần cho lý do chặn đơn và các màn công nợ. */
  limit_source?: 'unlimited' | 'customer' | 'revenue_avg' | 'tenant_default';
  /** Tổng giá trị đơn 3 tháng trước (không gồm tháng hiện tại) ÷ 3. */
  avg_order_total: number;
  total_orders_3m: number;
}

/** Item của bảng "Hạn mức nợ theo khách hàng" — GET /b2b/receivables/credit-limits (khách active có nợ hoặc TB 3 tháng > 0). */
export interface B2BCreditLimitRow {
  customer_id: string;
  company_name: string;
  /** TCR-1360: dùng để tìm kiếm khách hàng theo mã KH/SĐT. */
  customer_code?: string;
  customer_name?: string;
  phone?: string;
  current_debt: number;
  /** TCR-1883 — hạn mức hiệu lực (hạn mức riêng của khách, hoặc mặc định tenant). */
  credit_limit?: number;
  /** TCR-1883 — không bị áp hạn mức; chỉ đúng khi tenant tắt công tắc kiểm tra. */
  is_unlimited?: boolean;
  /** TCR-1883 — hạn mức lấy từ đâu: hạn mức riêng / TB 3 tháng / mặc định hệ thống. */
  limit_source?: 'unlimited' | 'customer' | 'revenue_avg' | 'tenant_default';
  /** Tổng giá trị đơn 3 tháng trước (không gồm tháng hiện tại) ÷ 3. */
  avg_order_3months: number;
  /** vd 1.21 nghĩa là 121% — NHÂN 100 khi hiển thị %. */
  ratio: number;
  status: 'ok' | 'warning';
  /**
   * TCR-1522 — có đang vượt hạn mức công nợ THẬT (current_debt > credit_limit
   * thật/fallback) không. KHÁC `status`/`ratio` ở trên (so nợ với
   * avg_order_3months — khái niệm khác, dùng cho bảng "Hạn mức nợ" ở trang
   * Receivables). Dùng field này cho badge "Quá công nợ" theo khách hàng ở
   * trang danh sách đơn (`useOrderColumns.tsx`) — nếu dùng nhầm `status`
   * sẽ sai badge vì 1 khách có thể `status: 'warning'` (nợ > avg 1 đơn) mà
   * KHÔNG vượt credit_limit thật, hoặc ngược lại.
   */
  is_over_limit: boolean;
}

export const b2bReceivableService = {
  list: (params: B2BReceivableQuery): Promise<ListResult<B2BReceivableType>> =>
    api.get('/b2b/receivables', { params }).then(unwrapList<B2BReceivableType>),

  overview: (params: B2BReceivableScopeQuery = {}): Promise<B2BReceivableOverview> =>
    api.get('/b2b/receivables/overview', { params }).then(unwrapObject<B2BReceivableOverview>),

  dashboard: (params: B2BReceivableScopeQuery = {}): Promise<B2BReceivableDashboard> =>
    api.get('/b2b/receivables/dashboard', { params }).then(unwrapObject<B2BReceivableDashboard>),

  aging: (params: B2BReceivableScopeQuery = {}): Promise<B2BAgingReport> =>
    api.get('/b2b/receivables/aging', { params }).then(unwrapObject<B2BAgingReport>),

  hasOverdue: (customerId: string): Promise<{ customer_id: string; has_overdue: boolean; overdue_count: number }> =>
    api.get(`/b2b/receivables/customers/${customerId}/overdue`).then(unwrapObject<{ customer_id: string; has_overdue: boolean; overdue_count: number }>),

  overdueAlerts: (params: B2BOverdueAlertsQuery = {}): Promise<B2BOverdueAlert[]> =>
    api.get('/b2b/receivables/overdue-alerts', { params }).then(unwrapObject<B2BOverdueAlert[]>),

  createDebtReminder: (customerId: string, note: string): Promise<B2BDebtReminder> =>
    api
      .post(`/b2b/receivables/customers/${customerId}/reminders`, { note })
      .then(unwrapObject<B2BDebtReminder>),

  debtReminders: (customerId: string): Promise<B2BDebtReminder[]> =>
    api.get(`/b2b/crm/customers/${customerId}/debt-reminders`)
      .then(unwrapObject<B2BDebtReminder[]>),

  staffOptions: (): Promise<B2BReceivableStaffOption[]> =>
    api.get('/b2b/receivables/staff-options').then(unwrapObject<B2BReceivableStaffOption[]>),

  creditLimit: (customerId: string): Promise<B2BCreditLimit> =>
    api.get(`/b2b/receivables/customers/${customerId}/credit-limit`).then(unwrapObject<B2BCreditLimit>),

  /** Bảng hạn mức nợ toàn bộ khách active — GET /b2b/receivables/credit-limits (endpoint mới TCR-461). */
  creditLimits: (params: B2BCreditLimitsQuery = {}): Promise<ListResult<B2BCreditLimitRow>> =>
    api.get('/b2b/receivables/credit-limits', { params }).then(unwrapList<B2BCreditLimitRow>),

  overLimitCustomerIds: (): Promise<string[]> =>
    api.get('/b2b/receivables/credit-limits/over-limit-customer-ids').then(unwrapObject<string[]>),

  listByCustomer: (customerId: string): Promise<ListResult<B2BReceivableType>> =>
    api.get('/b2b/receivables', { params: { customer_id: customerId, page_size: 100 } }).then(unwrapList<B2BReceivableType>),
};
